import { useState, useCallback, useRef } from "react"
import { useQuery } from "@tanstack/react-query"
import { Percent, Layers, Grid2x2Plus, Pickaxe, LandPlot, Download } from "lucide-react"
import { api } from "@/app/api/client"
import { KPICard } from "@/app/components/KPICard"
import { InventoryTable, type InventoryRow } from "@/app/components/InventoryTable"
import { StrDetailModal } from "@/app/components/StrDetailModal"
import { useClientTheme } from "@/app/components/ThemeContext"
import { useCurrentUser } from "@/app/hooks/useCurrentUser"
import { LoadingState } from "@/app/components/LoadingState"
import { PageHeader } from "@/app/components/PageHeader"
import { formatNumber, decimalToPercent } from "@/app/lib/format"
import { DIV_RED } from "@/app/lib/txColors"
import { secondaryButton } from "@/app/lib/buttonStyles"

interface InventoryResponse {
  data: InventoryRow[]
  meta: { total_count: number; page: number; per_page: number; total_pages: number }
}

export function InventoryPage() {
  const { theme } = useClientTheme()
  // Viewers get read-only access — CSV export is limited to owners/editors/staff.
  const { canWrite } = useCurrentUser()
  const [activeRow, setActiveRow] = useState<InventoryRow | null>(null)
  // Rows after the table's filters are applied — drives the stat cards.
  // null = the table hasn't reported yet (use all rows); [] = a real 0-match.
  const [visibleRows, setVisibleRows] = useState<InventoryRow[] | null>(null)
  // The table owns the current view (filters + sort); it parks its export fn
  // here so the header button can trigger it.
  const exportRef = useRef<(() => void) | null>(null)

  const { data, isLoading } = useQuery<InventoryResponse>({
    queryKey: ["inventories", "all"],
    queryFn: () => api.get<InventoryResponse>("/inventories?per_page=2000"),
  })

  const rows = data?.data ?? []

  // Stable callback so the table's reporting effect doesn't loop.
  const handleFiltered = useCallback((next: InventoryRow[]) => setVisibleRows(next), [])

  // KPIs computed over the filtered set (everything by default; narrows as the
  // user filters). Before the table's first report, fall back to all rows.
  const stat = visibleRows ?? rows
  const totalAcres = stat.reduce((s, r) => s + Number(r.net_acres), 0)
  // Net-acre-weighted portfolio NRI (positive acres as weights).
  const nriWeight = stat.reduce((s, r) => s + Math.max(Number(r.net_acres), 0), 0)
  const weightedNri =
    nriWeight > 0
      ? stat.reduce((s, r) => s + Number(r.weighted_nri) * Math.max(Number(r.net_acres), 0), 0) /
        nriWeight
      : 0
  const totalSections = stat.length
  const sectionsWithUnit = stat.filter((r) => r.wells.length > 0).length

  return (
    <div className="p-4 sm:px-6 sm:pt-5 sm:pb-6">
      <div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <PageHeader
            title="Master Inventory"
            subtitle="Aggregated net position by STR"
            theme={theme}
            icon={<LandPlot size={17} strokeWidth={1.75} />}
          />
          {/* mb-4 matches PageHeader's own bottom margin so the row aligns. */}
          <button
            type="button"
            onClick={() => exportRef.current?.()}
            disabled={!canWrite || stat.length === 0}
            className="btn mb-4"
            style={{
              ...secondaryButton(theme),
              opacity: !canWrite || stat.length === 0 ? 0.6 : 1,
              cursor: !canWrite || stat.length === 0 ? "default" : "pointer",
            }}
            title={
              !canWrite
                ? "Viewers have read-only access"
                : stat.length === 0
                  ? "No rows to export"
                  : "Export current view to CSV"
            }
          >
            <Download size={14} /> Export CSV
          </button>
        </div>

        {/* KPI cards — track the active filters */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 mb-4">
          <KPICard
            label="Net Acres (filtered)"
            value={formatNumber(totalAcres, 2)}
            valueColor={totalAcres < 0 ? DIV_RED : undefined}
            accentColor={theme.kpiAccents[0]}
            icon={<Grid2x2Plus size={16} strokeWidth={1.5} />}
          />
          <KPICard
            label="Weighted NRI"
            value={decimalToPercent(weightedNri, 2)}
            accentColor={theme.kpiAccents[1]}
            icon={<Percent size={16} strokeWidth={1.5} />}
          />
          <KPICard
            label="Total Sections"
            value={totalSections}
            accentColor={theme.kpiAccents[2]}
            icon={<Layers size={16} strokeWidth={1.5} />}
          />
          <KPICard
            label="Sections w/ Unit"
            value={sectionsWithUnit}
            accentColor={theme.kpiAccents[3]}
            icon={<Pickaxe size={16} strokeWidth={1.5} />}
          />
        </div>

        {isLoading ? (
          <LoadingState theme={theme} />
        ) : (
          <InventoryTable
            data={rows}
            onRowClick={setActiveRow}
            onFilteredChange={handleFiltered}
            exportRef={exportRef}
          />
        )}
      </div>

      {activeRow && <StrDetailModal row={activeRow} onClose={() => setActiveRow(null)} />}
    </div>
  )
}
